(ns manage.adset-decision-tree-page.property
  (:require [clojure.string :as str]
            [common.util :as util]
            [common.validators.core :as validators]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :as predicate-converter]
            [manage.adset-decision-tree-page.view.predicate-editor.validation
             :as predicate-validation]
            [manage.adset-decision-tree-page.view.side-panel.feed.converter
             :as feed-converter]
            [manage.adset-decision-tree-page.view.side-panel.poi.converter
             :as poi-converter]
            [mate.core :as mc]))

(def ad-variants-data-path [:ad-variants])
(def meta-pixel-data-path [:config :pixels])
(def trackers-data-path [:config :trackers])
(def tagging-data-path [:config :click-through-url-tagging])

(def trackers-schema
  [:sequential
   [:map
    [:type :any]
    [:value [:fn {:error/message "The placeholder expression should be a URL."}
             validators/url?]]]])

(def ad-variant-properties
  [{:id :ad-variant/tagging
    :context {:type :root-node}}
   {:id :ad-variant/trackers
    :context {:type :root-node}}
   {:id :ad-variant/pixel
    :adset-channel #{"meta"}}])

(def properties
  (->> [{:id :properties/default-has-no-properties-info
         :type :properties/info
         :title "Default outcomes can't be configured."
         :message "Default outcomes automatically apply when no specific conditions are met in your strategy node."}

        {:id :meta.facebook/platform-settings-warning
         :type :meta.facebook/platform-settings-warning}

        {:id :node/label
         :label "Name"
         :type :string
         :required true
         :data-path [:label]
         :schema [:string {:min 1
                           :error/message "The label should not be blank."}]}

        {:id :tiktok/timezone
         :label "Timezone"
         :type :autocomplete
         :options util/timezones
         :required true
         :data-path [:config :tiktok :timezone]
         :dissoc-on-nil true
         :schema [:some {:error/message "The timezone should not be blank."}]}

        {:id :meta/timezone
         :label "Timezone"
         :type :autocomplete
         :options util/timezones
         :required true
         :data-path [:config :meta :timezone]
         :dissoc-on-nil true
         :schema [:some {:error/message "The timezone should not be blank."}]}

        {:id :meta.facebook/adset-id
         :label "Meta Adset ID"
         :type :meta.facebook/adset-id-autocomplete
         :data-path [:config :meta :facebook-adset-id]
         :strategy-config-path [:facebook-adset-id]
         :dissoc-on-nil true}

        {:id :meta.facebook/lead-generation-form-id
         :label "Meta Form ID"
         :type :string
         :data-path [:config :meta :meta-lead-generation-form-id]
         :dissoc-on-nil true}

        {:id :tiktok/tiktok-ad-group-id
         :label "TikTok Ad Group ID"
         :type :tiktok/ad-group-autocomplete
         :data-path [:config :tiktok :tiktok-ad-group-id]
         :dissoc-on-nil true}

        {:id :meta/lat-lon
         :label "Latitude / Longitude"
         :type :lat-lon
         :required true
         :do-not-patch-invalid true
         :data-path [:config :meta]
         :form-convert {:to (fn [value] (update-vals value str))
                        :from (fn [value] (update-vals value (fnil parse-double "")))}
         :schema
         [:map
          [:lat [:double {:min -90 :max 90
                          :error/message "Should be a number between -90 and 90"}]]
          [:lon [:double {:min -180 :max 180
                          :error/message "Should be a number between -180 and 180"}]]]}

        {:id :targeting/predicate
         :label "Targeting predicate"
         :dissoc-on-nil true
         :type :targeting/predicate
         :required true
         :use-field-array true
         :form-convert {:to predicate-converter/->form
                        :from predicate-converter/<-form
                        :to-validation predicate-converter/->validation}
         :get-schema predicate-validation/get-schema-for-form
         :data-path [:targeting :predicate]
         :strategy-config-path [:predicate]}

        {:id :targeting/poi-predicate
         :label "Targeting predicate"
         :dissoc-on-nil true
         :type :targeting/poi-predicate
         :required true
         :use-field-array true
         :form-convert {:to predicate-converter/->form
                        :from predicate-converter/<-form
                        :to-validation predicate-converter/->validation}
         :get-schema predicate-validation/get-schema-for-form
         :data-path [:targeting :predicate]
         :strategy-config-path [:predicate]}

        {:id :ad-variant/tagging
         :label "Tagging"
         :type :ad-variant/tagging
         :use-field-array true
         :form-convert {:to (fn [value root-node]
                              (or value (get-in root-node tagging-data-path)))
                        :from (fn [value root-node]
                                (when-not (= value (get-in root-node tagging-data-path))
                                  value))}
         :data-path [:click-through-url-tagging]
         :dissoc-on-nil true}

        {:id :ad-variant/trackers
         :label "Trackers"
         :type :ad-variant/trackers
         :use-field-array true
         :form-convert {:to (fn [value root-node]
                              (or value (get-in root-node trackers-data-path)))
                        :from (fn [value root-node]
                                (when-not (= value (get-in root-node trackers-data-path))
                                  value))}
         :data-path [:trackers]
         :dissoc-on-nil true
         :do-not-patch-invalid true
         :schema trackers-schema}

        {:id :ad-variant/pixel
         :label "Meta Pixel"
         :type :ad-variant/pixel
         :form-convert {:to (fn [value] (-> value first :value))
                        :from (fn [value] (cond-> []
                                            (not (str/blank? value)) (conj {:value value})))}
         :data-path [:pixels]}

        {:id :feed/properties
         :type :feed/properties
         :data-path [:targeting :context]
         :strategy-config-path [:context]
         :strategy-required true
         :required true
         :form-convert {:to feed-converter/->form
                        :from feed-converter/<-form}
         :schema feed-converter/schema}

        {:id :feed/outcome-source
         :type :feed/outcome-source}

        {:id :poi/properties
         :type :poi/properties
         :data-path [:targeting :context]
         :strategy-config-path [:context]
         :strategy-required true
         :required true
         :do-not-patch-invalid true
         :form-convert {:to poi-converter/->form
                        :from poi-converter/<-form}
         :schema poi-converter/schema}]))

(def property-by-id (mc/index-by :id properties))

(defn resolve-property [property-instance]
  (let [property-defaults (get property-by-id (:id property-instance))]
    (into property-defaults property-instance)))

(defn resolve-properties [properties]
  (mapv resolve-property properties))

(defn filter-properties-by-channel [properties current-channel]
  (filter (fn [{:keys [adset-channel adset-channel-negated]}]
            (and (or (nil? adset-channel)
                     (contains? adset-channel current-channel))
                 (or (nil? adset-channel-negated)
                     (not (contains? adset-channel-negated current-channel)))))
          properties))

(defn filter-properties-by-flags [properties flag-values]
  (remove (fn [{:keys [feature-flag]}]
            (and feature-flag (not (get flag-values feature-flag))))
          properties))

(defn convert-properties
  [get-convert-fn properties properties-context form-data]
  (reduce (fn [node property]
            (let [property-context (get properties-context (:context property))
                  convert-fn (get-convert-fn property)]
              (cond-> node
                (some? convert-fn) (update-in (:data-path property)
                                              convert-fn property-context))))
          form-data
          properties))

(def ->form (partial convert-properties (comp :to :form-convert)))
(def <-form (partial convert-properties (comp :from :form-convert)))

(def form->validation (partial convert-properties
                               (fn [{{:keys [from to-validation]} :form-convert}]
                                 (or to-validation from))))

(def ->validation (partial convert-properties (comp :to-validation :form-convert)))
